import { useMutation, useQueryClient } from '@tanstack/react-query';
import { generateCourseLink, QUERY_KEYS, restoreCourseAccess, revokeCourseAccess, sendCourseMassEmail } from '@/lib';
import { CourseEmailPayload, fetchCourseAnalytics, fetchCourseFans, sendCourseLink } from '@/lib/api/courses/api';
import { useStanQuery } from '.';

/**
 * Mutation to send mass email to course fans
 */
export function useSendCourseMassEmail() {
    return useMutation({
        mutationKey: ['sendCourseMassEmail'],
        mutationFn: ({ courseId, payload }: { courseId: string; payload: CourseEmailPayload }) =>
            sendCourseMassEmail(courseId, payload),
    });
}

/**
 * Mutation to revoke course access for a user
 */
export function useRevokeCourseAccess(courseId: string) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: ['revokeCourseAccess'],
        mutationFn: (userFanId: string) => revokeCourseAccess(courseId, userFanId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.COURSE_ANALYTICS, courseId] });
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.COURSE_FANS, courseId] });
        },
    });
}

/**
 * Mutation to restore course access for a user
 */
export function useRestoreCourseAccess(courseId: string) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: ['restoreCourseAccess'],
        mutationFn: (userFanId: string) => restoreCourseAccess(courseId, userFanId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.COURSE_ANALYTICS, courseId] });
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.COURSE_FANS, courseId] });
        },
    });
}

/**
 * Mutation to generate a new course link
 */
export function useGenerateCourseLink() {
    return useMutation({
        mutationKey: ['generateCourseLink'],
        mutationFn: (slug: string) => generateCourseLink(slug),
    });
}

/**
 * Query to fetch course fans with optional filters
 */
export function useCourseFans(courseId: string, filterString?: string) {
    return useStanQuery(
        {
            queryKey: [QUERY_KEYS.COURSE_FANS, courseId, filterString],
            queryFn: () => fetchCourseFans(courseId, filterString),
            enabled: !!courseId,
            meta: { error: { sentryMessage: '[COURSE_ANALYTICS] failed-to-fetch-course-fans' } },
        },
        true
    );
}

export function useCourseAnalytics(courseId: string) {
    return useStanQuery(
        {
            queryKey: [QUERY_KEYS.COURSE_ANALYTICS, courseId],
            queryFn: () => fetchCourseAnalytics(courseId),
            enabled: !!courseId,
            meta: { error: { sentryMessage: '[COURSE_ANALYTICS] failed-to-fetch-course-analytics' } },
        },
        true
    );
}

// Course Link Mutation
export function useSendCourseLink() {
    return useMutation({
        mutationKey: [QUERY_KEYS.COURSE_LINK],
        mutationFn: (fanSlug: string) => sendCourseLink(fanSlug),
    });
}
